// The transcripts of a voice channel: what is said in it, speaker by speaker — a phrase's words as the
// server hears them, then the phrase written whole, with punctuation and capitals. Their list, the newest
// first, and one of them: its lines, older ones asked for as the view comes near the top. Anyone in the
// meeting reads them and downloads one as text, in the channel or not; the host and the moderators begin
// a new one, go on with one, and stop the one written — everyone sees that one is. A transcript gone on
// with another day says where that day begins. The view of a transcript follows the newest line while it
// is at the bottom, and stays where it is while the user reads older ones, as the chat's does.
import { Fragment } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";
import { controls, olderLines, showTranscript, transcribe, wholeTranscript } from "../meeting.ts";
import {
  channelName,
  channelOf,
  nameOf,
  state,
  transcriptsOf,
  type Line,
  type Transcripts as Known,
  type TranscriptSession,
} from "../store.ts";
import { Icon, IconButton } from "./Icon.tsx";

/** How near the top the view comes before older lines are asked for, and how near the bottom it is still
 * at the bottom, px. */
const NEAR_TOP = 80;
const NEAR_BOTTOM = 40;
/** A speaker's lines go under its first one while each comes within this long of the one before, ms. */
const TOGETHER = 60000;

export function Transcripts({ channel }: { channel: number }) {
  const t = transcriptsOf(channel);
  return (
    <div class="transcript">
      {t.shown === 0 ? <List channel={channel} t={t} /> : <Session key={t.shown} channel={channel} t={t} />}
    </div>
  );
}

/** The list: how many there are, and for the host and the moderators the button that begins a new one;
 * the one written, if one is, and the button to stop it; each transcript a card — when, how long — a click
 * opens it, and they go on with it. */
function List({ channel, t }: { channel: number; t: Known }) {
  const can = controls();
  const sessions = t.sessions === null ? null : [...t.sessions].sort((a, b) => b.number - a.number);
  return (
    <>
      <div class="transcript-head">
        <b>Расшифровки</b>
        {sessions !== null && sessions.length > 0 && <span class="pill">{sessions.length}</span>}
        {can && (
          <button class="primary new" title="Начать новую расшифровку" onClick={() => transcribe(channel, "new")}>
            <Icon name="plus" size={18} />
            <span>Новая</span>
          </button>
        )}
      </div>
      {t.writing !== 0 && (
        <div class="writing">
          <span class="rec on" />
          <span>Идёт расшифровка №{t.writing}</span>
          {can && (
            <button class="small" onClick={() => transcribe(channel, "stop")}>
              Остановить
            </button>
          )}
        </div>
      )}
      <div class="sessions">
        {sessions === null && <div class="empty">загружаю…</div>}
        {sessions !== null && sessions.length === 0 && <div class="empty">{emptyList(can)}</div>}
        {sessions?.map((s) => (
          <SessionRow key={s.number} channel={channel} s={s} live={s.number === t.writing} can={can} />
        ))}
      </div>
    </>
  );
}

/** What an empty list says. */
function emptyList(can: boolean): string {
  if (!can) return "Расшифровок пока нет. Их начинают владелец и модераторы пространства";
  return (
    "Расшифровок пока нет. Начните новую: речь в этом канале будет записываться текстом, по участникам. " +
    "Все в пространстве увидят, что она идёт"
  );
}

function SessionRow({ channel, s, live, can }: { channel: number; s: TranscriptSession; live: boolean; can: boolean }) {
  return (
    <div class={live ? "session live" : "session"}>
      <button class="session-open" onClick={() => showTranscript(channel, s.number)}>
        <span class="session-title">
          {live && <span class="rec on" />}
          <span>Расшифровка №{s.number}</span>
        </span>
        <span class="meta">
          {span(s)} · {count(s.lines, "фраза", "фразы", "фраз")}
          {live && " · идёт"}
        </span>
      </button>
      {can && !live && (
        <button
          class="small"
          title="Продолжить эту расшифровку"
          onClick={() => transcribe(channel, "continue", s.number)}
        >
          Продолжить
        </button>
      )}
    </div>
  );
}

/** A transcript: whether it is written now; going back to the list, downloading it, and for the host
 * and the moderators stopping it or going on with it; its lines. */
function Session({ channel, t }: { channel: number; t: Known }) {
  const box = useRef<HTMLDivElement>(null);
  /** Where the view was at the last drawing: its distance from the bottom, and the oldest line. */
  const fromBottom = useRef(0);
  const oldest = useRef(Infinity);
  const lines = [...t.lines.values()].sort((a, b) => a.seq - b.seq);
  useLayoutEffect(() => {
    const b = box.current!;
    const first = lines.length > 0 ? lines[0].seq : Infinity;
    if (fromBottom.current - b.clientHeight < NEAR_BOTTOM) b.scrollTop = b.scrollHeight;
    else if (first < oldest.current) b.scrollTop = b.scrollHeight - fromBottom.current;
    oldest.current = first;
    fromBottom.current = b.scrollHeight - b.scrollTop;
  });
  const scrolled = () => {
    const b = box.current!;
    fromBottom.current = b.scrollHeight - b.scrollTop;
    if (b.scrollTop <= NEAR_TOP) olderLines(channel);
  };
  const live = t.writing === t.shown;
  const can = controls();
  const info = t.sessions?.find((s) => s.number === t.shown);
  return (
    <>
      <div class="transcript-head session-head">
        <IconButton icon="back" title="Все расшифровки" onClick={() => showTranscript(channel, 0)} />
        {live && <span class="rec on" />}
        <b class="grow" title={info === undefined ? "" : span(info)}>
          №{t.shown}
          {info !== undefined && <small>{live ? "идёт" : day(info.started)}</small>}
        </b>
        {lines.some((l) => l.final) && (
          <IconButton
            icon="download"
            class={t.saving ? "lit" : ""}
            title={t.saving ? "Расшифровка загружается…" : "Скачать расшифровку"}
            onClick={() => void download(channel)}
          />
        )}
        {can && live && (
          <button class="small stop" onClick={() => transcribe(channel, "stop")}>
            Остановить
          </button>
        )}
        {can && !live && (
          <button
            class="small primary"
            title="Продолжить эту расшифровку"
            onClick={() => transcribe(channel, "continue", t.shown)}
          >
            Продолжить
          </button>
        )}
      </div>
      <div class="messages lines" ref={box} onScroll={scrolled}>
        {lines.length === 0 && <div class="empty">{t.asking ? "загружаю…" : emptySession(live)}</div>}
        {lines.map((l, i) => (
          <Fragment key={l.seq}>
            {i > 0 && !sameDay(lines[i - 1].at, l.at) && <div class="line-day">{longDay(l.at)}</div>}
            <LineView line={l} same={i > 0 && together(lines[i - 1], l)} />
          </Fragment>
        ))}
      </div>
    </>
  );
}

/** What an empty transcript says. */
function emptySession(live: boolean): string {
  if (live) return "Говорите в голосовом канале: здесь появится текст, по участникам";
  return "В этой расшифровке нет ни одной фразы";
}

/** Whether line `l` goes under `before`, the line before it: the same speaker's, soon after, the same
 * day. */
function together(before: Line, l: Line): boolean {
  return before.user === l.user && l.at - before.at < TOGETHER && sameDay(before.at, l.at);
}

/** Whether `a` and `b` (ms since 1970) are of one day of the user's clock. */
function sameDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** A line: who said it and when — unless it goes under the speaker's line before — and the words; a line
 * under way, paler. */
function LineView({ line, same }: { line: Line; same: boolean }) {
  const own = line.user === state.user;
  const classes = ["msg", "line", own && "mine", !line.final && "live", same && "same"];
  return (
    <div class={classes.filter((x) => x).join(" ")} title={clock(line.at)}>
      {!same && (
        <div class="who">
          {own ? "вы" : speaker(line)} <time>{clock(line.at)}</time>
        </div>
      )}
      <div class="text">{line.text}</div>
    </div>
  );
}

/** Who said line `l`: the name the speaker had then, or has now. */
function speaker(l: Line): string {
  return l.name !== "" ? l.name : nameOf(l.user);
}

/** The time of `ms` (since 1970), as the user's clock shows it. */
function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/** The day of `ms`, short: "5 окт."; long: "5 октября". */
function day(ms: number): string {
  return new Date(ms).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function longDay(ms: number): string {
  return new Date(ms).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

/** When a transcript was written: its day and hours, from its beginning to its last line. */
function span(s: TranscriptSession): string {
  const hm = (ms: number) => new Date(ms).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const from = `${day(s.started)}, ${hm(s.started)}`;
  if (s.last <= s.started) return from;
  return sameDay(s.started, s.last) ? `${from}–${hm(s.last)}` : `${from} – ${day(s.last)}, ${hm(s.last)}`;
}

/** `n` and the word for it in Russian: "1 фраза", "2 фразы", "5 фраз". */
function count(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? one : d >= 2 && d <= 4 && (h < 12 || h > 14) ? few : many;
  return `${n} ${word}`;
}

/** The transcript shown, whole, as a text file: a line each — when, who, what — and the date where
 * another day begins. */
async function download(channel: number): Promise<void> {
  const t = transcriptsOf(channel);
  const session = t.shown;
  await wholeTranscript(channel);
  if (state.meeting === null || t.shown !== session) return;
  const m = state.meeting;
  const c = channelOf(channel, "voice");
  const where = c === null ? m.title : `${m.title}, ${channelName(c)}`;
  const lines = [...t.lines.values()].filter((l) => l.final).sort((a, b) => a.seq - b.seq);
  const date = lines.length > 0 ? new Date(lines[0].at).toLocaleDateString("ru-RU") : "";
  const said = lines.flatMap((l, i) => {
    const line = `[${clock(l.at)}] ${speaker(l)}: ${l.text}`;
    if (i === 0 || sameDay(lines[i - 1].at, l.at)) return [line];
    return ["", new Date(l.at).toLocaleDateString("ru-RU"), line];
  });
  const text = [`${where} — расшифровка №${session} — ${date}`, "", ...said].join("\n");
  const url = URL.createObjectURL(new Blob([text + "\n"], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `Расшифровка №${session} — ${where} — ${date}.txt`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
